import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Modal,
  TextInput,
  Alert,
  RefreshControl,
  FlatList,
  Animated,
  useWindowDimensions,
} from 'react-native';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { feeAPI } from '../services/api';
import { Logger } from '../utils/logger';
import { Colors, Spacing, FontSize, BorderRadius } from '../theme/colors';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { printFeeReceipt } from '../components/fee/printFeeReceipt';
import { Toast } from '../components/common/Toast';
import { WebPageShell } from '../components/common/WebPageShell';
import { PayItem, payItemsFrom, selectedTotal, selectionSummary } from '../utils/feePaySelection';
import { InvoicePicker } from '../components/fee/InvoicePicker';
import { InstallmentList } from '../components/fee/InstallmentList';
import { feeLineLabel, feeLineText } from '../utils/feeLineLabel';
import { useTheme, ThemeColors } from '../context/ThemeContext';

// ─── Types ────────────────────────────────────────────────────────────────────
//
// Source of truth: GET /StudentApplication/{studentId}/invoices, returning
// StudentInvoiceListItemDto[] — the exact same endpoint the proven staff web
// frontend (src/frontend's StudentFees.tsx) uses to render its fee table. Each
// row carries a real InvoiceId, which is what makes "Pay Now" actually work.

type FeeStatus = 'paid' | 'unpaid' | 'overdue' | 'partial';
type PaymentMode = 'online' | 'cash' | 'cheque' | 'dd' | 'upi' | 'card' | 'netbanking' | 'wallet';

// Screen-local, display-ready installment shape mapped from the real
// StudentInvoiceListItemDto[] entries (see mapInstallments below).
interface DisplayInstallment {
  id: string;
  invoiceId: string;
  invoiceNumber: string;
  installmentName: string;
  amount: number;
  paidAmount: number;
  balanceAmount: number;
  discount: number;
  dueDate: string;
  status: FeeStatus;
  type: string;
}

interface FeeSummary {
  totalFee: number;
  totalPaid: number;
  dueNow: number;
  totalOverdue: number;
  upcoming: number;
  discount: number;
  outstandingBalance: number;
  currentDue: number;
  nextDue?: { amount: number; date: string; installmentName: string };
  planStartDate?: string;
  planEndDate?: string;
  academicYear: string;
  installmentCount: number;
  installmentAmount: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (amount: number) =>
  `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const fmtDate = (iso: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isOverdue = (dueDate: string, status: FeeStatus) =>
  status !== 'paid' && new Date(dueDate) < new Date();

const isFuture = (dueDate: string, status: FeeStatus) =>
  status !== 'paid' && !!dueDate && new Date(dueDate) > new Date();

// "Month N" is the Nth instalment of the year; the due date is the real month. The fee
// name and any discount note are kept (utils/feeLineLabel) - this used to show the month alone.
const displayInstallmentName = (inst: DisplayInstallment) => feeLineText(inst.installmentName, inst.dueDate);

const STATUS_CONFIG: Record<FeeStatus, { label: string; bg: string; text: string }> = {
  paid: { label: 'Paid', bg: '#E8F8EE', text: Colors.success },
  unpaid: { label: 'Due', bg: '#FFF8E7', text: Colors.warning },
  overdue: { label: 'Overdue', bg: '#FEE9E7', text: Colors.error },
  partial: { label: 'Partially Paid', bg: Colors.primaryLight, text: Colors.primary },
};

const UPCOMING_CONFIG = { label: 'Upcoming', bg: '#EEF2FF', text: '#4F46E5' };

// Row-level status display: a future-dated unpaid/partial installment reads
// as "Upcoming" rather than "Due", so parents aren't told something is owed
// before its due date.
function getStatusDisplay(inst: DisplayInstallment) {
  if (isFuture(inst.dueDate, inst.status) && inst.status !== 'partial') {
    return UPCOMING_CONFIG;
  }
  return STATUS_CONFIG[inst.status];
}

// ─── Mapping (real API responses → screen display shapes) ─────────────────────

// Maps StudentInvoiceListItemDto[] into the screen's display-ready installments.
// Real invoice rows have no installment "name" — derive one from Description, else
// a generic "Installment N" fallback. Discount isn't part of this DTO (it lives on
// the breakdown endpoint instead), so it's always 0 here.
function mapInstallments(invoices: any[]): DisplayInstallment[] {
  return (invoices ?? []).map((f, i) => {
    const amount = f.GrandTotal ?? f.grandTotal ?? 0;
    const paidAmount = f.PaidAmount ?? f.paidAmount ?? 0;
    const balanceAmount = f.BalanceAmount ?? f.balanceAmount ?? (amount - paidAmount);
    const isPaid = f.IsPaid ?? f.isPaid ?? false;
    const dueDate = f.DueDate ?? f.dueDate ?? f.InvoiceDate ?? f.invoiceDate ?? '';
    const type = f.RefType ?? f.refType ?? '';

    let status: FeeStatus;
    if (isPaid || balanceAmount <= 0) {
      status = 'paid';
    } else if (paidAmount > 0 && balanceAmount > 0) {
      status = 'partial';
    } else if (dueDate && new Date(dueDate) < new Date()) {
      status = 'overdue';
    } else {
      status = 'unpaid';
    }

    return {
      id: f.StudentInvoiceId ?? f.studentInvoiceId ?? String(i),
      invoiceId: f.InvoiceId ?? f.invoiceId ?? '',
      invoiceNumber: f.InvoiceNumber ?? f.invoiceNumber ?? '',
      installmentName: f.Description ?? f.description ?? (type || `Installment ${i + 1}`),
      amount,
      paidAmount,
      balanceAmount,
      discount: 0,
      dueDate,
      status,
      type,
    };
  });
}

function buildSummary(fees: DisplayInstallment[]): FeeSummary {
  const total = fees.reduce((s, f) => s + f.amount, 0);
  const paid = fees.reduce((s, f) => s + f.paidAmount, 0);
  const discount = fees.reduce((s, f) => s + f.discount, 0);
  const overdue = fees
    .filter(f => f.status === 'overdue')
    .reduce((s, f) => s + f.balanceAmount, 0);
  const upcoming = fees
    .filter(f => isFuture(f.dueDate, f.status))
    .reduce((s, f) => s + f.balanceAmount, 0);
  // "Due now" is what's owed today: unpaid/partial installments that aren't
  // overdue and aren't dated in the future.
  const dueNow = fees
    .filter(f => f.status !== 'paid' && f.status !== 'overdue' && !isFuture(f.dueDate, f.status))
    .reduce((s, f) => s + f.balanceAmount, 0);
  const outstandingBalance = overdue + dueNow + upcoming;

  const nextUnpaid = fees
    .filter(f => f.status !== 'paid')
    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())[0];

  const dueDates = fees.map(f => f.dueDate).filter(Boolean).sort();
  const planStartDate = dueDates[0];
  const planEndDate = dueDates[dueDates.length - 1];
  const years = dueDates
    .map(d => new Date(d).getFullYear())
    .filter(y => !isNaN(y));
  const minYear = years.length ? Math.min(...years) : undefined;
  const maxYear = years.length ? Math.max(...years) : undefined;
  const academicYear = minYear
    ? (maxYear && maxYear !== minYear ? `${minYear}–${maxYear}` : `${minYear}`)
    : '';

  return {
    totalFee: total,
    totalPaid: paid,
    dueNow,
    totalOverdue: overdue,
    upcoming,
    discount,
    outstandingBalance,
    currentDue: overdue + dueNow,
    nextDue: nextUnpaid
      ? {
        amount: nextUnpaid.balanceAmount,
        date: nextUnpaid.dueDate,
        installmentName: nextUnpaid.installmentName,
      }
      : undefined,
    planStartDate,
    planEndDate,
    academicYear,
    installmentCount: fees.length,
    installmentAmount: fees.length ? fees[0].amount : 0,
  };
}

// Maps StudentFeeBreakdownDto.FeeBreakdown[] into the simple {head, amount} rows
// used by the breakup card in the detail modal.
function mapBreakdown(breakdown: any[]): { head: string; amount: number }[] {
  return (breakdown ?? []).map(b => ({
    head: b.ComponentName ?? b.componentName ?? '',
    amount: b.Amount ?? b.amount ?? 0,
  }));
}

// ─── Sub-components ───────────────────────────────────────────────────────────

// ─── Pay Modal ────────────────────────────────────────────────────────────────
//
// installment.invoiceId now comes from the real StudentInvoiceListItemDto, so this
// calls the same POST /StudentFee/{studentId}/invoice/{invoiceId}/payment endpoint
// the proven staff web frontend uses to record a payment.

interface PayModalProps {
  /** Every pending invoice (utils/feePaySelection). */
  items: PayItem[];
  /** Ticked when the dialog opens: all of them from "Pay now", one from a row. */
  initialSelected: string[];
  onClose: () => void;
  onPaymentSuccess: () => void;
}

function PayModal({ items, initialSelected, onClose, onPaymentSuccess }: PayModalProps) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initialSelected));
  const amount = selectedTotal(items, selected);
  const toggle = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const closeWithAnimation = () => {
    onClose();
  };

  const handleAction = () => {
    if (selected.size === 0) return;
    onPaymentSuccess();
  };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={closeWithAnimation}>
      <TouchableOpacity style={styles.payModalOverlay} activeOpacity={1} onPress={closeWithAnimation}>
        <View style={styles.payModalContent}>
          <TouchableOpacity activeOpacity={1} style={{ flexShrink: 1, width: '100%' }}>
            <View style={styles.modalHeader}>
              <View>
                <Text style={styles.modalTitle}>Pay fees</Text>
                <Text style={styles.paySubtitleText} numberOfLines={2}>{selectionSummary(items, selected)}</Text>
              </View>
              <TouchableOpacity onPress={closeWithAnimation} style={styles.modalClose}>
                <FeatherIcon name="x" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <ScrollView
              contentContainerStyle={styles.modalBody}
              showsVerticalScrollIndicator={true}
              indicatorStyle="black"
              persistentScrollbar={true}
              fadingEdgeLength={50}
            >
              <View style={{ marginBottom: 8 }}>
                <Text style={styles.newPaySubTitle}>Amount due · KYD</Text>
              </View>

              {/* Amount Box */}
              <View style={styles.newAmountBox}>
                <Text style={styles.newAmountText}>{fmt(amount)}</Text>
              </View>

              {/* Which invoices the amount covers - ticked to include */}
              <View style={{ marginBottom: 20 }}>
                <InvoicePicker
                  items={items}
                  selected={selected}
                  onToggle={toggle}
                  onSelectAll={all => setSelected(new Set(all ? items.map(i => i.id) : []))}
                  format={fmt}
                />
              </View>

              {/* Offline Payment Information */}
              <Text style={styles.newSectionLabel}>Payment Instructions</Text>
              <View style={styles.newModeCard}>
                <View style={styles.newModeIconBox}>
                  <FeatherIcon name="info" size={20} color={colors.textSecondary} />
                </View>
                <View style={styles.newModeInfo}>
                  <Text style={styles.newModeTitle}>Pay at School</Text>
                  <Text style={[styles.newModeSub, { lineHeight: 18 }]}>
                    Online payments are currently unavailable. Please visit the school administration counter to complete your fee payment via Cash, Cheque, or Demand Draft.
                  </Text>
                </View>
              </View>

            </ScrollView>

            {/* Action button */}
            <View style={styles.newModalFooter}>
              <TouchableOpacity
                style={styles.newPayBtn}
                onPress={handleAction}>
                <Text style={styles.newPayBtnText}>
                  {selected.size === 0 ? 'Select an invoice to pay' : `Pay at School · ${fmt(amount)}`}
                </Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    </Modal>
  );
}

// ─── Installment Detail Modal ─────────────────────────────────────────────────

interface DetailModalProps {
  installment: DisplayInstallment;
  breakup: { head: string; amount: number }[];
  onClose: () => void;
  onPay: () => void;
}

function StatusBadge({ installment }: { installment: DisplayInstallment }) {
  const config = getStatusDisplay(installment);
  return (
    <View style={{ paddingHorizontal: 12, paddingVertical: 4, borderRadius: 12, backgroundColor: config.bg, justifyContent: 'center', alignItems: 'center' }}>
      <Text style={{ fontSize: 12, fontWeight: '700', color: config.text }}>{config.label}</Text>
    </View>
  );
}

function InstallmentDetailModal({ installment, breakup, onClose, onPay }: DetailModalProps) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const isPaid = installment.status === 'paid';
  const overdue = isOverdue(installment.dueDate, installment.status);

  const closeWithAnimation = () => {
    onClose();
  };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={closeWithAnimation}>
      <TouchableOpacity style={styles.detailModalOverlay} activeOpacity={1} onPress={closeWithAnimation}>
        <View style={styles.detailModalContent}>
          <TouchableOpacity activeOpacity={1} style={{ flexShrink: 1, width: '100%' }}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Fee Details</Text>
              <TouchableOpacity onPress={closeWithAnimation} style={styles.modalClose}>
                <FeatherIcon name="x" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <ScrollView
              contentContainerStyle={styles.modalBody}
              showsVerticalScrollIndicator={true}
              indicatorStyle="black"
              persistentScrollbar={true}
              fadingEdgeLength={50}
            >
              {/* Status card */}
              <View style={[styles.detailStatusCard, { borderLeftColor: isPaid ? Colors.success : overdue ? Colors.error : Colors.warning }]}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.detailInstallmentName}>{displayInstallmentName(installment)}</Text>
                </View>
                <StatusBadge installment={installment} />
              </View>

              {/* Key details */}
              <View style={styles.detailCard}>
                <View style={styles.detailRow}>
                  <Text style={styles.detailRowLabel}>Total Amount</Text>
                  <Text style={styles.detailRowValue}>{fmt(installment.amount)}</Text>
                </View>
                {installment.discount > 0 && (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailRowLabel}>Discount</Text>
                    <Text style={[styles.detailRowValue, { color: Colors.success }]}>{fmt(installment.discount)}</Text>
                  </View>
                )}
                {installment.paidAmount > 0 && (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailRowLabel}>Amount Paid</Text>
                    <Text style={[styles.detailRowValue, { color: Colors.success }]}>{fmt(installment.paidAmount)}</Text>
                  </View>
                )}
                {installment.balanceAmount > 0 && (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailRowLabel}>Balance Due</Text>
                    <Text style={[styles.detailRowValue, { color: Colors.error }]}>
                      {fmt(installment.balanceAmount)}
                    </Text>
                  </View>
                )}
                <View style={styles.detailRow}>
                  <Text style={styles.detailRowLabel}>Due Date</Text>
                  <Text style={[styles.detailRowValue, overdue && { color: Colors.error }]}>
                    {installment.dueDate ? fmtDate(installment.dueDate) : '—'} {overdue ? '(Overdue)' : ''}
                  </Text>
                </View>
              </View>

              {/* Breakup — from getFeeBreakdown, when available for this student */}
              {breakup.length > 0 && (
                <>
                  <Text style={styles.detailSectionLabel}>Fee Breakup</Text>
                  <View style={styles.breakupCard}>
                    {breakup.map((b, i) => (
                      <View key={i} style={[styles.breakupRow, i < breakup.length - 1 && styles.breakupDivider]}>
                        <Text style={styles.breakupHead}>{b.head}</Text>
                        <Text style={styles.breakupAmt}>{fmt(b.amount)}</Text>
                      </View>
                    ))}
                  </View>
                </>
              )}
            </ScrollView>

            {/* Action buttons */}
            {/* {!isPaid && (
          <View style={styles.modalFooter}>
            <TouchableOpacity style={styles.payBtn} onPress={onPay}>
              <Text style={styles.payBtnText}>Pay {fmt(installment.balanceAmount)}</Text>
            </TouchableOpacity>
          </View>
        )} */}
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    </Modal>
  );
}

// ─── All Installments Modal ───────────────────────────────────────────────────

interface AllInstallmentsModalProps {
  installments: DisplayInstallment[];
  onClose: () => void;
  onInstallmentPress: (installment: DisplayInstallment) => void;
  /** Paid and partly paid instalments open their receipt. */
  onReceipt: (installment: DisplayInstallment) => void;
}

function AllInstallmentsModal({ installments, onClose, onInstallmentPress, onReceipt }: AllInstallmentsModalProps) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const closeWithAnimation = () => {
    onClose();
  };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={closeWithAnimation}>
      <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={closeWithAnimation}>
        <View style={[styles.modalContent, { maxHeight: '90%' }]}>
          <TouchableOpacity activeOpacity={1} style={{ flexShrink: 1, width: '100%' }}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>All Installments</Text>
              <TouchableOpacity onPress={closeWithAnimation} style={styles.modalClose}>
                <FeatherIcon name="x" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <ScrollView
              contentContainerStyle={styles.modalBody}
              showsVerticalScrollIndicator={true}
              indicatorStyle="black"
              persistentScrollbar={true}
              fadingEdgeLength={50}
            >
              <InstallmentList
                installments={installments}
                nameOf={displayInstallmentName}
                formatMoney={fmt}
                formatDate={fmtDate}
                onPress={onInstallmentPress}
                onReceipt={onReceipt}
              />
            </ScrollView>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    </Modal>
  );
}

// NOTE: The backend has no receipt-list or receipt-download endpoint at all, so the
// old "Receipts" tab / download action has been removed rather than left as a
// silent no-op. See feeAPI comment in services/api.ts.

// ─── Main Screen ──────────────────────────────────────────────────────────────

export function FeeScreen() {
  const { selectedStudent } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [installments, setInstallments] = useState<DisplayInstallment[]>([]);
  const [breakup, setBreakup] = useState<{ head: string; amount: number }[]>([]);
  const [summary, setSummary] = useState<FeeSummary | null>(null);
  const [selectedInstallment, setSelectedInstallment] = useState<DisplayInstallment | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showAllInstallments, setShowAllInstallments] = useState(false);
  // The invoices ticked when the Pay fees dialog opens; null = closed.
  const [payIds, setPayIds] = useState<string[] | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { width: winWidth } = useWindowDimensions();
  const isMobile = winWidth < 768;

  const loadData = useCallback(async () => {
    if (!selectedStudent) {
      setLoading(false);
      setRefreshing(false);
      return;
    }
    Logger.info('Fee', `Loading fee data for student ${selectedStudent.id}`);
    try {
      // Same endpoint the proven staff web frontend uses to render its fee table —
      // a flat, complete list of real invoices with real per-row InvoiceIds.
      const { data: invoiceData } = await feeAPI.getStudentInvoices(selectedStudent.id);
      const invoiceList = Array.isArray(invoiceData) ? invoiceData : invoiceData?.data ?? [];
      const mapped = mapInstallments(invoiceList);
      setInstallments(mapped);
      setSummary(buildSummary(mapped));
      Logger.debug('Fee', `Loaded ${mapped.length} fee installments`);

      // Breakdown drives the "Fee Breakup" section in the detail/pay modals.
      // Failure here shouldn't block the main schedule from showing.
      try {
        const { data: breakdownData } = await feeAPI.getFeeBreakdown(selectedStudent.id);
        setBreakup(mapBreakdown(breakdownData?.FeeBreakdown ?? breakdownData?.feeBreakdown ?? []));
      } catch (breakdownErr) {
        Logger.warn('Fee', 'Failed to load fee breakdown', breakdownErr);
        setBreakup([]);
      }
    } catch (err) {
      Logger.error('Fee', 'Failed to load fee data', err);
      Alert.alert('Error', 'Could not load fee details. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedStudent]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Pays a single row - the amount box shows exactly that installment's
  // balance, same as the row's own "Pay Now" / "Pay Early" button promised.
  // The invoices the Pay fees dialog offers, named and dated as on this screen.
  const payItems = useMemo(
    () => payItemsFrom(installments, displayInstallmentName, i => `Due ${fmtDate(i.dueDate)}`),
    [installments],
  );

  // A row's own Pay Now / Pay Early: just that invoice ticked, others still offered.
  const openPayForInstallment = (inst: DisplayInstallment) => {
    setSelectedInstallment(inst);
    setPayIds([inst.id]);
  };

  // "Pay now" under Total outstanding: every pending invoice ticked, so the amount is the
  // outstanding total; the parent can untick any (it used to be only what was due now).
  const openPayForOutstanding = () => {
    if (payItems.length === 0) return;
    setPayIds(payItems.map(i => i.id));
  };

  const printInstallmentReceipt = (inst: DisplayInstallment) => {
    if (!selectedStudent) return;
    printFeeReceipt(inst, selectedStudent.name);
  };

  // The page keeps its header while it loads, with the standard loader inside it.
  if (loading) {
    return <WebPageShell title="Fees & payments"><LoadingSpinner message="Loading fee details..." /></WebPageShell>;
  }

  return (
    <WebPageShell title="Fees & payments">
      <View style={[styles.container, { flexGrow: 1, flex: undefined }]}>
        <View style={[styles.scroll, { flexGrow: 1, flex: undefined, paddingHorizontal: 20, paddingTop: 10, paddingBottom: 28 }]}>


          {summary && (
            <>
              {/* Fee Plan Summary */}
              <View style={[styles.planCard, isMobile && styles.planCardMobile]}>
                <View style={styles.planHeaderRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.planName}>Monthly Fee Plan</Text>
                    {!!summary.academicYear && (
                      <Text style={styles.planYear}>Academic Year {summary.academicYear}</Text>
                    )}
                  </View>
                  {selectedStudent && (
                    <View style={styles.planStudentBox}>
                      <Text style={styles.planStudentName}>{selectedStudent.name}</Text>
                      <Text style={styles.planStudentClass}>{selectedStudent.class}</Text>
                    </View>
                  )}
                </View>

                <View style={styles.planGrid}>
                  <View style={styles.planGridItem}>
                    <Text style={styles.planGridLabel}>Total Annual Fee</Text>
                    <Text style={styles.planGridValue}>{fmt(summary.totalFee)}</Text>
                  </View>
                  <View style={styles.planGridItem}>
                    <Text style={styles.planGridLabel}>Installments</Text>
                    <Text style={styles.planGridValue}>{summary.installmentCount}</Text>
                  </View>
                  <View style={styles.planGridItem}>
                    <Text style={styles.planGridLabel}>Installment Amount</Text>
                    <Text style={styles.planGridValue}>{fmt(summary.installmentAmount)}</Text>
                  </View>
                  <View style={styles.planGridItem}>
                    <Text style={styles.planGridLabel}>Plan Period</Text>
                    <Text style={styles.planGridValue}>
                      {fmtDate(summary.planStartDate || '')} – {fmtDate(summary.planEndDate || '')}
                    </Text>
                  </View>
                  <View style={styles.planGridItem}>
                    <Text style={styles.planGridLabel}>Next Due Date</Text>
                    <Text style={styles.planGridValue}>
                      {summary.nextDue ? fmtDate(summary.nextDue.date) : '—'}
                    </Text>
                  </View>
                </View>
              </View>

              {/* Financial Summary Cards */}
              <View style={styles.summaryGrid}>
                {[
                  { label: 'Total Fee', value: summary.totalFee, color: colors.textPrimary },
                  { label: 'Paid', value: summary.totalPaid, color: Colors.success },
                  { label: 'Due Now', value: summary.dueNow, color: Colors.warning },
                  { label: 'Overdue', value: summary.totalOverdue, color: Colors.error },
                  { label: 'Upcoming', value: summary.upcoming, color: '#4F46E5' },
                  { label: 'Discount', value: summary.discount, color: Colors.success },
                  { label: 'Outstanding Balance', value: summary.outstandingBalance, color: colors.primary },
                ].map(card => (
                  <View key={card.label} style={[styles.summaryCard, isMobile && styles.summaryCardMobile]}>
                    <Text style={styles.summaryCardLabel}>{card.label}</Text>
                    <Text style={[styles.summaryCardValue, { color: card.color }]}>{fmt(card.value)}</Text>
                  </View>
                ))}
              </View>

              {/* Pay action */}
              {summary.currentDue > 0 && (
                <TouchableOpacity style={styles.payNowBtn} onPress={openPayForOutstanding}>
                  <Text style={styles.payNowBtnText}>
                    Pay Due Amount – KYD {fmt(summary.dueNow + summary.totalOverdue).replace('$', '')}
                  </Text>
                </TouchableOpacity>
              )}
            </>
          )}

          {installments.length > 0 && (
            <>
              <Text style={styles.sectionTitle}>Installment Schedule</Text>
              <View style={styles.tableCard}>
                {/* Seven columns need ~760px: wherever the card is narrower (a phone, a tablet) they
                    scroll sideways instead of squeezing the amounts together; wider, they fill it. */}
                <ScrollView horizontal showsHorizontalScrollIndicator contentContainerStyle={styles.tableScrollContent}>
                <View style={styles.tableScrollInner}>
                <View style={styles.tableHeaderRow}>
                  <Text style={[styles.tableHeaderCell, styles.tableColInstallment]}>Installment</Text>
                  <Text style={[styles.tableHeaderCell, styles.tableColDate]}>Due Date</Text>
                  <Text style={[styles.tableHeaderCell, styles.tableColAmount]}>Amount</Text>
                  <Text style={[styles.tableHeaderCell, styles.tableColAmount]}>Paid</Text>
                  <Text style={[styles.tableHeaderCell, styles.tableColAmount]}>Balance</Text>
                  <Text style={[styles.tableHeaderCell, styles.tableColStatus]}>Status</Text>
                  <Text style={[styles.tableHeaderCell, styles.tableColAction]}>Action</Text>
                </View>
                {installments.map((inst, idx, arr) => {
                  const statusCfg = getStatusDisplay(inst);
                  const future = isFuture(inst.dueDate, inst.status);
                  return (
                    <View key={inst.id} style={[styles.tableRow, idx !== arr.length - 1 && styles.structureDivider]}>
                      <View style={styles.tableColInstallment}>
                        <Text style={[styles.tableCell, styles.tableCellStrong]}>{feeLineLabel(inst.installmentName, inst.dueDate).title}</Text>
                        {feeLineLabel(inst.installmentName, inst.dueDate).notes.map(n => (
                          <Text key={n} style={styles.tableCellNote}>{n}</Text>
                        ))}
                      </View>
                      <Text style={[styles.tableCell, styles.tableColDate]}>{fmtDate(inst.dueDate)}</Text>
                      <Text style={[styles.tableCell, styles.tableColAmount]}>{fmt(inst.amount)}</Text>
                      <Text style={[styles.tableCell, styles.tableColAmount]}>{fmt(inst.paidAmount)}</Text>
                      <Text style={[styles.tableCell, styles.tableColAmount]}>{fmt(inst.balanceAmount)}</Text>
                      <View style={[styles.tableColStatus, { alignItems: 'flex-start' }]}>
                        <View style={{ paddingHorizontal: 10, paddingVertical: 3, borderRadius: 10, backgroundColor: statusCfg.bg }}>
                          <Text style={{ fontSize: 11, fontWeight: '700', color: statusCfg.text }}>{statusCfg.label}</Text>
                        </View>
                      </View>
                      <View style={[styles.tableColAction, { alignItems: 'flex-start' }]}>
                        {inst.status === 'paid' ? (
                          <TouchableOpacity
                            style={styles.printIconBtn}
                            onPress={() => printInstallmentReceipt(inst)}
                            accessibilityLabel={`Print receipt for ${displayInstallmentName(inst)}`}
                          >
                            <FeatherIcon name="printer" size={15} color={colors.primary} />
                          </TouchableOpacity>
                        ) : (
                          <TouchableOpacity
                            style={[styles.payPillBtn, future && styles.payPillBtnOutline]}
                            onPress={() => openPayForInstallment(inst)}
                          >
                            <Text style={[styles.payPillBtnText, future && styles.payPillBtnTextOutline]}>
                              {future ? 'Pay Early' : 'Pay Now'}
                            </Text>
                          </TouchableOpacity>
                        )}
                      </View>
                    </View>
                  );
                })}
                </View>
                </ScrollView>
              </View>
            </>
          )}

          <View style={styles.actionButtonsRow}>
            <TouchableOpacity style={styles.actionBtn} onPress={() => setShowAllInstallments(true)}>
              <Text style={styles.actionBtnText}>Installments</Text>
            </TouchableOpacity>
            {/* No backend endpoint exists for auto-pay — this had no onPress
                at all, so it was a dead button that looked clickable.
                Disabled and greyed out rather than removed. */}
            <TouchableOpacity style={[styles.actionBtn, styles.actionBtnDisabled]} disabled>
              <Text style={[styles.actionBtnText, styles.actionBtnTextDisabled]}>Set auto-pay</Text>
            </TouchableOpacity>
          </View>

          <Text style={styles.sectionTitle}>Payments & Receipts</Text>
          <View style={styles.historyList}>
            {installments.filter(i => i.status === 'paid').length === 0 ? (
              <Text style={styles.noHistoryText}>No past payments</Text>
            ) : (
              installments.filter(i => i.status === 'paid').map(inst => (
                <View key={inst.id} style={styles.historyCard}>
                  <View style={styles.historyIconBox}>
                    <FeatherIcon name="check" size={14} color="#16A34A" />
                  </View>
                  <View style={styles.historyInfo}>
                    <Text style={styles.historyTitle}>{displayInstallmentName(inst)}</Text>
                    <Text style={styles.historyDate}>
                      {fmtDate(inst.dueDate)} · {inst.invoiceNumber || `REC-${inst.id.slice(0, 8).toUpperCase()}`}
                    </Text>
                  </View>
                  <View style={styles.historyRight}>
                    <Text style={styles.historyAmount}>{fmt(inst.paidAmount)}</Text>
                    <TouchableOpacity
                      style={styles.printIconBtn}
                      onPress={() => printInstallmentReceipt(inst)}
                      accessibilityLabel={`Print receipt for ${displayInstallmentName(inst)}`}
                    >
                      <FeatherIcon name="printer" size={15} color={colors.primary} />
                    </TouchableOpacity>
                  </View>
                </View>
              ))
            )}
          </View>
          {/* <View style={{ height: 32 }} /> */}
        </View>

        {/* Modals */}
        {showAllInstallments && (
          <AllInstallmentsModal
            installments={installments}
            onClose={() => setShowAllInstallments(false)}
            onInstallmentPress={(inst) => {
              setShowAllInstallments(false);
              setSelectedInstallment(inst);
              setShowDetail(true);
            }}
            onReceipt={printInstallmentReceipt}
          />
        )}

        {showDetail && selectedInstallment && (
          <InstallmentDetailModal
            installment={selectedInstallment}
            breakup={breakup}
            onClose={() => setShowDetail(false)}
            onPay={() => {
              setShowDetail(false);
              openPayForInstallment(selectedInstallment);
            }}
          />
        )}

        {payIds && (
          <PayModal
            items={payItems}
            initialSelected={payIds}
            onClose={() => setPayIds(null)}
            onPaymentSuccess={() => {
              setPayIds(null);
              loadData();
              setShowToast(true);
            }}
          />
        )}

        <Toast
          visible={showToast}
          message={toastMessage}
          type={toastType}
          onDismiss={() => setShowToast(false)}
        />
      </View>
    </WebPageShell>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: 12 },
  loadingText: { fontSize: FontSize.md, color: colors.textSecondary },
  scroll: { flex: 1 },

  scrollContent: { paddingHorizontal: 20, paddingTop: 10, paddingBottom: 28 },
  screenTitle: { fontSize: 20, fontWeight: '800', color: colors.textPrimary, marginBottom: 20 },

  // Outstanding Card
  outstandingCard: {
    backgroundColor: colors.primary, // Blue background
    borderRadius: 24,
    padding: 24,
    marginBottom: 24,
    elevation: 6,
    shadowColor: colors.primary,
    shadowOpacity: 0.3,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 6 },
  },
  outstandingCardMobile: { padding: 18, borderRadius: 18 },
  outstandingLabel: { fontSize: 13, color: 'rgba(255,255,255,0.8)', fontWeight: '600', marginBottom: 8 },
  outstandingRow: { flexDirection: 'row', alignItems: 'center' },
  outstandingAmount: { fontSize: 36, fontWeight: '800', color: '#FFF', letterSpacing: -0.5 },
  outstandingAmountMobile: { fontSize: 28 },
  dueDateRow: { flexDirection: 'row', alignItems: 'center', marginTop: 8, marginBottom: 20 },
  dueDateText: { fontSize: 13, color: '#E2E8F0', fontWeight: '500', marginLeft: 6 },
  payNowBtn: {
    backgroundColor: '#F97316',
    borderRadius: 16,
    paddingVertical: 14,
    alignItems: 'center',
    shadowColor: '#EA580C',
    shadowOpacity: 0.3,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 3 },
    elevation: 4,
    // Room before the "Installment Schedule" heading, which sat right against it.
    marginBottom: 24,
  },
  payNowBtnText: { color: '#FFF', fontSize: 16, fontWeight: '800' },

  sectionTitle: { fontSize: 16, fontWeight: '800', color: colors.textPrimary, marginBottom: 16 },

  // Fee Plan Summary
  planCard: {
    backgroundColor: colors.card,
    borderRadius: 20,
    padding: 24,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: colors.border,
  },
  planCardMobile: { padding: 16, borderRadius: 16 },
  planHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 12,
  },
  planName: { fontSize: 18, fontWeight: '800', color: colors.textPrimary },
  planYear: { fontSize: 13, color: colors.textSecondary, marginTop: 4, fontWeight: '600' },
  planStudentBox: { alignItems: 'flex-end' },
  planStudentName: { fontSize: 14, fontWeight: '700', color: colors.textPrimary },
  planStudentClass: { fontSize: 12, color: colors.textMuted, marginTop: 2 },
  planGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 20 },
  planGridItem: { minWidth: 140 },
  planGridLabel: { fontSize: 12, color: colors.textMuted, fontWeight: '600', marginBottom: 4 },
  planGridValue: { fontSize: 14, color: colors.textPrimary, fontWeight: '700' },

  // Financial Summary Cards
  summaryGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 20 },
  summaryCard: {
    flexGrow: 1,
    flexBasis: 150,
    backgroundColor: colors.card,
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: colors.border,
  },
  summaryCardMobile: { flexBasis: '47%' },
  summaryCardLabel: { fontSize: 12, color: colors.textSecondary, fontWeight: '600', marginBottom: 6 },
  summaryCardValue: { fontSize: 18, fontWeight: '800' },

  // Installment Schedule table
  tableCard: {
    backgroundColor: colors.card,
    borderRadius: 20,
    marginBottom: 28,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.border,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: colors.background,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  tableHeaderCell: { fontSize: 11, fontWeight: '700', color: colors.textMuted, textTransform: 'uppercase' },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  tableCell: { fontSize: 13, color: colors.textSecondary },
  tableCellStrong: { fontWeight: '700', color: colors.textPrimary },
  tableCellNote: { fontSize: 11.5, fontWeight: '600', color: colors.success, marginTop: 2 },
  tableScrollContent: { flexGrow: 1 },
  tableScrollInner: { minWidth: 760, flexGrow: 1 },
  tableColInstallment: { flexBasis: 170, flexGrow: 1.4 },
  tableColDate: { flexBasis: 100, flexGrow: 1 },
  tableColAmount: { flexBasis: 90, flexGrow: 1 },
  tableColStatus: { flexBasis: 100, flexGrow: 1 },
  tableColAction: { flexBasis: 90, flexGrow: 1 },

  // Row action buttons (Pay Now / Pay Early / print receipt)
  payPillBtn: {
    backgroundColor: colors.primary,
    borderRadius: 999,
    paddingVertical: 7,
    paddingHorizontal: 14,
  },
  payPillBtnOutline: {
    backgroundColor: 'transparent',
    borderWidth: 1.5,
    borderColor: colors.primary,
  },
  payPillBtnText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#FFF',
  },
  payPillBtnTextOutline: {
    color: colors.primary,
  },
  printIconBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primaryLight,
  },

  // Fee Structure
  structureCard: {
    backgroundColor: colors.card,
    borderRadius: 20,
    padding: 20,
    marginBottom: 20,
    elevation: 2,
    shadowColor: colors.shadow,
    shadowOpacity: 0.04,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
  },
  structureRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 14 },
  structureDivider: { borderBottomWidth: 1, borderBottomColor: colors.divider },
  structureHead: { fontSize: 14, color: colors.textSecondary, fontWeight: '500' },
  structureAmt: { fontSize: 14, color: colors.textPrimary, fontWeight: '700' },
  structureTotalDivider: { borderTopWidth: 1, borderTopColor: colors.divider, marginTop: 4, paddingTop: 16, borderBottomWidth: 0 },
  structureTotalLabel: { fontSize: 15, color: colors.textPrimary, fontWeight: '800' },
  structureTotalAmt: { fontSize: 16, color: colors.primary, fontWeight: '800' },

  // Action Buttons
  actionButtonsRow: { flexDirection: 'row', gap: 12, marginBottom: 28 },
  actionBtn: {
    flex: 1,
    backgroundColor: colors.card,
    borderRadius: 16,
    paddingVertical: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    elevation: 1,
    shadowColor: colors.shadow,
    shadowOpacity: 0.02,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
  },
  actionBtnText: { fontSize: 14, fontWeight: '700', color: colors.textSecondary },
  actionBtnDisabled: { backgroundColor: colors.background, opacity: 0.6 },
  actionBtnTextDisabled: { color: colors.textMuted },

  // Payment History
  historyList: { marginBottom: 0 },
  noHistoryText: { fontSize: 14, color: colors.textMuted, textAlign: 'center', marginTop: 12 },
  historyCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.card,
    borderRadius: 20,
    padding: 16,
    marginBottom: 12,
    elevation: 1,
    shadowColor: colors.shadow,
    shadowOpacity: 0.03,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
  },
  historyIconBox: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#F0FDF4',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
  },
  historyInfo: { flex: 1 },
  historyTitle: { fontSize: 14, fontWeight: '700', color: colors.textPrimary, marginBottom: 4 },
  historyDate: { fontSize: 12, color: colors.textMuted, fontWeight: '500' },
  historyRight: { alignItems: 'flex-end' },
  historyAmount: { fontSize: 15, fontWeight: '800', color: colors.textPrimary, marginBottom: 4 },
  receiptText: { fontSize: 12, color: colors.primary, fontWeight: '700' },
  // Modal shared
  modalOverlay: {
    flex: 1,
    backgroundColor: colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  modalContent: {
    backgroundColor: colors.background,
    borderRadius: 16,
    maxHeight: '90%',
    overflow: 'hidden',
    width: '100%',
    maxWidth: 500,
  },
  // Centered Popup Styles
  payModalOverlay: {
    flex: 1,
    backgroundColor: colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
  },
  payModalContent: {
    backgroundColor: colors.surface,
    borderRadius: 20,
    width: '90%',
    maxWidth: 500,
    maxHeight: '88%',
    overflow: 'hidden',
  },
  detailModalOverlay: {
    flex: 1,
    backgroundColor: colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
  },
  detailModalContent: {
    backgroundColor: colors.surface,
    borderRadius: 20,
    width: '90%',
    maxWidth: 600,
    maxHeight: '88%',
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.surface,
    paddingHorizontal: Spacing.md,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  modalClose: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.background,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalCloseText: { fontSize: FontSize.md, color: colors.textSecondary, fontWeight: '700' },
  modalTitle: { fontSize: FontSize.lg, fontWeight: '700', color: colors.textPrimary },
  paySubtitleText: { fontSize: 12, color: colors.textSecondary, marginTop: 2, fontWeight: '500' },
  modalBody: { padding: Spacing.md, paddingBottom: Spacing.xl },
  modalFooter: {
    padding: Spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },

  // New Pay Modal Styles
  sheetHandleContainer: {
    alignItems: 'center',
    paddingVertical: 12,
  },
  sheetHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.textMuted,
  },
  newPayTitle: {
    fontSize: 20,
    fontWeight: '800',
    color: colors.textPrimary,
  },
  newPaySubTitle: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 4,
  },
  newAmountBox: {
    backgroundColor: colors.card,
    borderRadius: 16,
    paddingVertical: 24,
    alignItems: 'center',
    marginBottom: 24,
    shadowColor: colors.shadow,
    shadowOpacity: 0.05,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 4 },
    elevation: 2,
  },
  newAmountText: {
    fontSize: 32,
    fontWeight: '800',
    color: colors.textPrimary,
  },
  newSectionLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.textSecondary,
    marginBottom: 12,
  },
  newModeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.card,
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: colors.divider,
  },
  newModeCardSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.primaryLight,
  },
  newModeIconBox: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: colors.divider,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  newModeIconText: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.textSecondary,
  },
  newModeInfo: {
    flex: 1,
  },
  newModeTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.textPrimary,
    marginBottom: 4,
  },
  newModeSub: {
    fontSize: 12,
    color: colors.textMuted,
  },
  newRadioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: colors.border,
    justifyContent: 'center',
    alignItems: 'center',
  },
  newRadioOuterSelected: {
    borderColor: colors.primary,
  },
  newRadioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.primary,
  },
  newSecureNote: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 24,
    gap: 6,
  },
  newSecureText: {
    fontSize: 12,
    color: colors.textMuted,
    fontWeight: '500',
  },
  newModalFooter: {
    padding: 24,
    paddingTop: 12,
    backgroundColor: colors.background,
  },
  newPayBtn: {
    backgroundColor: colors.primary,
    borderRadius: 16,
    paddingVertical: 18,
    alignItems: 'center',
  },
  newPayBtnDisabled: {
    backgroundColor: colors.textMuted,
  },
  newPayBtnText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#FFF',
  },

  // Detail modal
  detailStatusCard: {
    backgroundColor: colors.surface,
    borderRadius: BorderRadius.md,
    padding: Spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: Spacing.sm,
    borderLeftWidth: 4,
  },
  detailInstallmentName: { fontSize: FontSize.lg, fontWeight: '700', color: colors.textPrimary },
  detailCard: {
    backgroundColor: colors.surface,
    borderRadius: BorderRadius.md,
    overflow: 'hidden',
    marginBottom: Spacing.sm,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: Spacing.md,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  detailRowLabel: { fontSize: FontSize.sm, color: colors.textSecondary },
  detailRowValue: { fontSize: FontSize.sm, fontWeight: '600', color: colors.textPrimary },
  detailSectionLabel: {
    fontSize: FontSize.md,
    fontWeight: '700',
    color: colors.textPrimary,
    marginTop: Spacing.md,
    marginBottom: Spacing.sm,
  },
  breakupCard: {
    backgroundColor: colors.surface,
    borderRadius: BorderRadius.md,
    padding: Spacing.md,
    marginBottom: Spacing.md,
  },
  breakupRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  breakupDivider: {
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  breakupHead: {
    fontSize: FontSize.sm,
    color: colors.textSecondary,
  },
  breakupAmt: {
    fontSize: FontSize.sm,
    fontWeight: '600',
    color: colors.textPrimary,
  },
  breakdownBox: {
    backgroundColor: colors.card,
    borderRadius: 12,
    paddingHorizontal: Spacing.md,
    marginBottom: 20,
  },
  breakdownRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
  },
});
